import { routePaths } from "../constants/routes";

const internalLinks = [
  { label: "Learn Automation Testing", to: routePaths.automationTestingTopic },
  { label: "Practice Automation Testing", to: routePaths.automationTesting },
  { label: "Learn API Testing", to: routePaths.apiTesting },
  { label: "QA Interview Questions", to: routePaths.interviewQuestions },
  { label: "Join Live QA Course", to: routePaths.enroll },
];

function makePage({
  slug,
  title,
  description,
  keyword,
  audience,
  examples,
  types,
  advantages,
  disadvantages,
  questions,
  faqs,
}) {
  return {
    slug,
    path: `/${slug}`,
    title,
    description,
    keyword,
    audience,
    internalLinks,
    sections: [
      {
        heading: `What is ${keyword}?`,
        paragraphs: [
          `${keyword} is an important software testing topic for learners who want practical QA knowledge instead of only theory. In real projects, testers use this concept to understand requirements, identify risk, design test coverage, and communicate clearly with developers, business teams, and managers.`,
          `For ${audience}, this topic is useful because interviewers often ask scenario-based questions. A strong answer should explain the meaning, where it is used, how it improves quality, and what mistakes beginners should avoid.`,
        ],
      },
      {
        heading: "Why is it important?",
        paragraphs: [
          `This topic matters because software testing is not only about finding bugs. Good QA work helps teams prevent defects, reduce release risk, improve user experience, and build confidence before production deployment.`,
          `When you understand ${keyword}, you can write better test cases, ask stronger requirement questions, and explain your testing approach in interviews with real examples.`,
        ],
      },
      {
        heading: "Types and key areas",
        list: types,
      },
      {
        heading: "Real-time examples",
        paragraphs: examples,
      },
      {
        heading: "Advantages",
        list: advantages,
      },
      {
        heading: "Disadvantages or limitations",
        list: disadvantages,
      },
      {
        heading: "Interview questions",
        list: questions,
      },
      {
        heading: "FAQ",
        faqs,
      },
    ],
  };
}

const defaultAdvantages = [
  "Improves test coverage and helps teams identify important risk areas.",
  "Makes interview answers stronger because you can explain practical usage.",
  "Helps testers connect requirements, scenarios, defects, and business impact.",
  "Supports better communication between QA, development, product, and business teams.",
];

const defaultDisadvantages = [
  "Beginners may memorize definitions without understanding real project usage.",
  "The topic can be misunderstood if examples are not connected to actual application flows.",
  "Poor documentation or unclear requirements can reduce the effectiveness of this concept.",
];

const defaultFaqs = (keyword) => [
  {
    question: `Is ${keyword} important for freshers?`,
    answer: `Yes. ${keyword} is useful for freshers because it appears in interviews and helps build practical QA thinking.`,
  },
  {
    question: `Can I learn ${keyword} without coding?`,
    answer: `Yes, most QA fundamentals can be learned without coding first. Coding becomes more important when you move into automation testing.`,
  },
  {
    question: `How should I explain ${keyword} in an interview?`,
    answer: `Start with a simple definition, explain why it matters, then give one real-time project example and mention common mistakes.`,
  },
];

export const seoContentPages = [
  makePage({
    slug: "top-50-selenium-interview-questions",
    title: "Top 50 Selenium Interview Questions and Answers",
    description: "Prepare for Selenium interviews with important questions on locators, waits, WebDriver, frames, alerts, actions, TestNG, and automation frameworks.",
    keyword: "Selenium Interview Questions",
    audience: "automation testing learners and job seekers",
    types: ["WebDriver basics", "Locator strategy", "Wait handling", "Frames and alerts", "Actions class", "TestNG and framework design"],
    examples: [
      "Example: If a login button appears after an API response, Selenium scripts should use explicit wait instead of hard sleep.",
      "Example: If an element is inside an iframe, the test must switch to that frame before locating the element.",
      "Example: For dynamic elements, testers should prefer stable attributes, accessible labels, or relative XPath instead of brittle absolute XPath.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: [
      "What is Selenium WebDriver?",
      "Difference between findElement and findElements?",
      "What is implicit wait and explicit wait?",
      "How do you handle alerts in Selenium?",
      "How do you switch to frames?",
      "What is Page Object Model?",
      "How do you handle dynamic XPath?",
      "What is stale element exception?",
    ],
    faqs: defaultFaqs("Selenium Interview Questions"),
  }),
  makePage({
    slug: "manual-testing-real-time-examples",
    title: "Manual Testing Real Time Examples for Beginners",
    description: "Learn manual testing with real-time examples for login, ecommerce, payment, search, registration, validation, and defect reporting.",
    keyword: "Manual Testing Real Time Examples",
    audience: "manual testing freshers",
    types: ["Login testing", "Registration testing", "Search testing", "Cart and checkout testing", "Payment validation", "Error message testing"],
    examples: [
      "Login example: Test valid login, invalid password, blank email, locked account, remember me, and forgot password link.",
      "Ecommerce example: Add product to cart, update quantity, apply coupon, remove item, and verify final price.",
      "Form example: Check mandatory fields, invalid email format, phone length, duplicate user, and success message.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["How do you test a login page?", "How do you write test cases for ecommerce?", "What is a test scenario?", "How do you report a defect?"],
    faqs: defaultFaqs("Manual Testing Real Time Examples"),
  }),
  makePage({
    slug: "api-testing-interview-questions",
    title: "API Testing Interview Questions and Answers",
    description: "API testing interview questions covering HTTP methods, status codes, Postman, authentication, headers, payloads, schema validation, and real-time scenarios.",
    keyword: "API Testing Interview Questions",
    audience: "API testing learners",
    types: ["HTTP methods", "Status codes", "Headers and authentication", "Request body validation", "Response schema validation", "Negative API testing"],
    examples: [
      "Example: For a create user API, validate 201 status, response body, database entry, duplicate email error, and required field validation.",
      "Example: For a secure API, test missing token, expired token, invalid token, and user without permission.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is API testing?", "Difference between PUT and PATCH?", "What is 401 vs 403?", "How do you test API authentication?", "What is JSON schema validation?"],
    faqs: defaultFaqs("API Testing Interview Questions"),
  }),
  makePage({
    slug: "test-case-vs-test-scenario",
    title: "Test Case vs Test Scenario - Difference with Examples",
    description: "Understand test case vs test scenario with simple examples, templates, advantages, disadvantages, and interview questions.",
    keyword: "Test Case vs Test Scenario",
    audience: "manual testing beginners",
    types: ["High-level scenarios", "Detailed test cases", "Positive cases", "Negative cases", "Boundary cases"],
    examples: [
      "Scenario: Verify user can login. Test cases: valid login, invalid password, blank fields, locked account, SQL injection attempt.",
      "Scenario: Verify cart calculation. Test cases: add one item, add multiple items, apply coupon, remove item, update quantity.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is a test scenario?", "What is a test case?", "Can one scenario have many test cases?", "Which comes first, scenario or test case?"],
    faqs: defaultFaqs("Test Case vs Test Scenario"),
  }),
  makePage({
    slug: "bug-life-cycle-explained",
    title: "Bug Life Cycle Explained with Real Project Example",
    description: "Learn bug life cycle statuses such as new, assigned, open, fixed, retest, verified, reopened, rejected, deferred, and closed.",
    keyword: "Bug Life Cycle",
    audience: "QA freshers and manual testers",
    types: ["New", "Assigned", "Open", "Fixed", "Retest", "Verified", "Reopened", "Closed", "Rejected", "Deferred"],
    examples: [
      "Example: QA reports login failure as New. Lead assigns it to a developer. Developer fixes it. QA retests and verifies. Then the defect is closed.",
      "Example: If the same issue still exists after fix, QA marks it Reopened with evidence and updated steps.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is bug life cycle?", "When do you reopen a bug?", "Difference between rejected and deferred?", "What details should a bug report include?"],
    faqs: defaultFaqs("Bug Life Cycle"),
  }),
  makePage({
    slug: "sdlc-vs-stlc",
    title: "SDLC vs STLC - Difference with Examples",
    description: "Learn the difference between SDLC and STLC, phases, examples, advantages, disadvantages, and interview questions.",
    keyword: "SDLC vs STLC",
    audience: "software testing beginners",
    types: ["Requirement analysis", "Design", "Development", "Testing", "Deployment", "Maintenance", "Test planning", "Test closure"],
    examples: [
      "SDLC covers the complete software development journey from requirement to maintenance.",
      "STLC focuses specifically on testing activities such as test planning, test case design, execution, defect reporting, and closure.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is SDLC?", "What is STLC?", "How are SDLC and STLC connected?", "Which STLC phase comes after test planning?"],
    faqs: defaultFaqs("SDLC vs STLC"),
  }),
  makePage({
    slug: "smoke-vs-regression-testing",
    title: "Smoke vs Regression Testing - Difference with Examples",
    description: "Compare smoke testing and regression testing with examples, use cases, advantages, limitations, and interview questions.",
    keyword: "Smoke vs Regression Testing",
    audience: "manual and automation testers",
    types: ["Build verification smoke tests", "Full regression tests", "Partial regression", "Automated regression suite"],
    examples: [
      "Smoke testing example: Verify app launches, login works, dashboard opens, and critical navigation is not broken after a new build.",
      "Regression testing example: After coupon logic changes, verify cart, checkout, payment, invoice, and order history still work.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is smoke testing?", "What is regression testing?", "Which is done first?", "Can smoke testing be automated?"],
    faqs: defaultFaqs("Smoke vs Regression Testing"),
  }),
  makePage({
    slug: "black-box-vs-white-box-testing",
    title: "Black Box vs White Box Testing - Difference with Examples",
    description: "Understand black box and white box testing differences, examples, advantages, disadvantages, and interview questions.",
    keyword: "Black Box vs White Box Testing",
    audience: "QA learners",
    types: ["Functional black box testing", "Boundary value testing", "Decision coverage", "Statement coverage", "Code path testing"],
    examples: [
      "Black box example: Test login using input/output behavior without knowing source code.",
      "White box example: Check if every branch in the authentication function is covered by unit tests.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is black box testing?", "What is white box testing?", "Who performs white box testing?", "Can QA perform black box testing without code?"],
    faqs: defaultFaqs("Black Box vs White Box Testing"),
  }),
  makePage({
    slug: "functional-vs-non-functional-testing",
    title: "Functional vs Non Functional Testing - Difference with Examples",
    description: "Learn functional and non functional testing differences with examples such as usability, performance, security, reliability, and compatibility.",
    keyword: "Functional vs Non Functional Testing",
    audience: "testing beginners",
    types: ["Functional testing", "Usability testing", "Performance testing", "Security testing", "Compatibility testing", "Reliability testing"],
    examples: [
      "Functional example: Verify user can reset password using registered email.",
      "Non functional example: Verify password reset email is generated within acceptable response time and the page works on mobile.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is functional testing?", "What is non functional testing?", "Give examples of both.", "Is performance testing functional or non functional?"],
    faqs: defaultFaqs("Functional vs Non Functional Testing"),
  }),
  makePage({
    slug: "test-plan-template-download",
    title: "Test Plan Template Download with Explanation",
    description: "Learn test plan template sections including scope, objective, resources, schedule, risks, deliverables, entry criteria, and exit criteria.",
    keyword: "Test Plan Template",
    audience: "manual testers and QA leads",
    types: ["Objective", "Scope", "Test strategy", "Resources", "Schedule", "Risks", "Deliverables", "Entry and exit criteria"],
    examples: [
      "Example: For an ecommerce release, the test plan includes checkout scope, supported browsers, payment risks, test data, and release sign-off criteria.",
      "Example: A small sprint test plan may include only scope, test cases, environment, assignee, and dates.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is a test plan?", "Who prepares test plan?", "What are entry and exit criteria?", "What is test strategy?"],
    faqs: defaultFaqs("Test Plan Template"),
  }),
  makePage({
    slug: "selenium-xpath-tutorial",
    title: "Selenium XPath Tutorial for Beginners",
    description: "Learn XPath for Selenium automation with absolute XPath, relative XPath, contains, starts-with, text, axes, and dynamic locator examples.",
    keyword: "Selenium XPath Tutorial",
    audience: "automation testing beginners",
    types: ["Absolute XPath", "Relative XPath", "contains()", "starts-with()", "text()", "XPath axes", "Dynamic XPath"],
    examples: [
      "Example: Use //input[@name='email'] for a stable email field locator.",
      "Example: Use //button[contains(text(),'Login')] when button text contains extra spaces or dynamic labels.",
      "Example: Use parent-child axes when the target element has no stable id but nearby label text is stable.",
    ],
    advantages: defaultAdvantages,
    disadvantages: ["XPath can become slow or brittle if written as a long absolute path.", "Dynamic UI changes can break weak locators.", "Beginners often overuse XPath when CSS selectors may be simpler."],
    questions: ["What is XPath?", "Absolute vs relative XPath?", "How do you handle dynamic XPath?", "What is contains in XPath?"],
    faqs: defaultFaqs("Selenium XPath Tutorial"),
  }),
  makePage({
    slug: "rest-api-testing-guide",
    title: "REST API Testing Guide for Beginners",
    description: "Learn REST API testing with endpoints, methods, headers, payloads, authentication, status codes, assertions, and test scenarios.",
    keyword: "REST API Testing Guide",
    audience: "API testing beginners",
    types: ["GET API testing", "POST API testing", "PUT/PATCH testing", "DELETE testing", "Authentication testing", "Schema validation"],
    examples: [
      "Example: Test GET /users returns 200, correct list structure, pagination metadata, and no sensitive fields.",
      "Example: Test POST /users with valid payload, missing mandatory fields, duplicate email, invalid token, and invalid role.",
    ],
    advantages: defaultAdvantages,
    disadvantages: defaultDisadvantages,
    questions: ["What is REST API?", "How do you test GET API?", "What is payload?", "What is bearer token?"],
    faqs: defaultFaqs("REST API Testing Guide"),
  }),
  makePage({
    slug: "postman-tutorial-for-beginners",
    title: "Postman Tutorial for Beginners",
    description: "Learn Postman basics including collections, environments, variables, auth, tests, scripts, runners, and API documentation.",
    keyword: "Postman Tutorial",
    audience: "manual testers moving into API testing",
    types: ["Collections", "Environment variables", "Authorization", "Pre-request scripts", "Tests tab", "Collection runner"],
    examples: [
      "Example: Store baseUrl as an environment variable and reuse it across all API requests.",
      "Example: Add a test script to validate status code 200 and response body contains expected user id.",
    ],
    advantages: defaultAdvantages,
    disadvantages: ["Postman is powerful but can become messy without collection naming standards.", "It does not replace deeper API test strategy.", "Team collaboration needs clear environment and variable discipline."],
    questions: ["What is Postman?", "What is collection?", "What is environment variable?", "How do you validate status code in Postman?"],
    faqs: defaultFaqs("Postman Tutorial"),
  }),
  makePage({
    slug: "automation-testing-tools-list",
    title: "Automation Testing Tools List for QA Engineers",
    description: "Explore popular automation testing tools including Selenium, Playwright, Cypress, Appium, Postman, Rest Assured, JMeter, and TestNG.",
    keyword: "Automation Testing Tools List",
    audience: "QA engineers choosing automation tools",
    types: ["UI automation tools", "API automation tools", "Mobile automation tools", "Performance tools", "Test management tools"],
    examples: [
      "Selenium is widely used for browser automation across Java, Python, C#, and JavaScript.",
      "Playwright is useful for modern web apps with strong auto-waiting and cross-browser support.",
      "Postman and Rest Assured are commonly used for API testing depending on manual or coded automation needs.",
    ],
    advantages: defaultAdvantages,
    disadvantages: ["Tool choice depends on project needs, not popularity alone.", "Every tool requires maintenance strategy.", "Wrong tool selection can increase automation cost."],
    questions: ["Which automation tool is best?", "Selenium vs Playwright?", "Which tool is used for API testing?", "Which tool is used for mobile testing?"],
    faqs: defaultFaqs("Automation Testing Tools List"),
  }),
  makePage({
    slug: "qa-career-roadmap-2026",
    title: "QA Career Roadmap 2026 - Manual to Automation Testing",
    description: "Follow a QA career roadmap for 2026 covering manual testing, SQL, API testing, automation, tools, projects, resume, and interviews.",
    keyword: "QA Career Roadmap 2026",
    audience: "freshers, job switchers, and manual testers",
    types: ["Manual testing foundation", "SQL basics", "API testing", "Automation testing", "Projects", "Resume and interview preparation"],
    examples: [
      "Beginner path: Learn SDLC, STLC, test cases, bug reports, Jira, and basic SQL.",
      "Intermediate path: Add API testing, Postman, database validation, and real-time scenarios.",
      "Automation path: Learn locators, Selenium or Playwright, TestNG or similar framework concepts, Git, and CI basics.",
    ],
    advantages: defaultAdvantages,
    disadvantages: ["Trying to learn every tool at once can slow progress.", "Skipping manual testing fundamentals makes automation weak.", "A roadmap works only if you build practice projects."],
    questions: ["How do I become a QA engineer in 2026?", "Should I learn manual testing first?", "Is automation testing required?", "What projects should QA freshers build?"],
    faqs: defaultFaqs("QA Career Roadmap 2026"),
  }),
];

export function getSeoContentPageBySlug(slug) {
  return seoContentPages.find((page) => page.slug === slug);
}
